import {WixSdk} from 'js-sdk-wrapper';
import {SiteStorage} from 'music-shared/dist/src/site-storage/site-storage';
import * as PL from 'wix-music-widget';
import {BI_ERRORS} from '../logging/errors';
import {logError, logPlayerStarted} from '../logging/logging';
import {logPlayerEvents} from '../statistics/send-event';
import {Settings} from '../types/settings';
import {areObjectsEqual, cloneObject} from '../utils/objects';
import {writeOops} from '../utils/oops';
import {getUrlPrefixes} from '../utils/url';
import {getBiToken, getInstanceId} from '../utils/wix-sdk';
import {DEMO_ALBUM_ID, NO_ALBUM_ID} from './constants';
import {setWidgetHeightOld} from './deprecated';
import {layoutValues} from './layout-values';
import {getLocale} from './locale';
import {subscribeToPlayerEvents} from './subscribe-to-player-events';
import {getNormalizedWidth, resizeOnLayoutChange, resizeOnReInit, resizeOnTrackStyleChange} from './widget-resizer';

let settings: Settings;
let prevSettings: Settings = {};
let player;
const siteStorage = new SiteStorage('wmp');

interface InitOptions {
  forceUpdate?: boolean,
  experimentsPromise?: Promise<any>,
  isAppOnPage?: boolean
}

export function initPlayer(newSettings?, {forceUpdate = false, experimentsPromise, isAppOnPage}: InitOptions = {}) {
  const userId = WixSdk.Utils.getUid();
  const instanceId = getInstanceId();
  const biToken = getBiToken();
  const compId = WixSdk.Utils.getCompId();
  const locale = getLocale();

  if (newSettings) {
    settings = newSettings;
  }

  if (!settings) {
    return;
  }
  settings.booleans = settings.booleans || {};
  return Promise.all([
      getAlbumId(),
      getPaymentsId(),
      getIsGetWindowEnabled(),
      WixSdk.getSiteInfo(),
      WixSdk.Activities.getUserSessionToken()
        .catch(reason => {
          logError(BI_ERRORS.tpa.wix.activities.getUserSessionToken, {reason});
          const uid = WixSdk.Utils.getUid() || WixSdk.Utils.getInstanceValue('aid');
          return uid ? uid.replace(/-/g, '') : 'asaasasasasasasasasasasasasasasasa';
        }),
      WixSdk.getBoundingRectAndOffsets(),
      experimentsPromise
    ])
    .then(([
             albumId,
             paymentsId,
             isGetWindowEnabled,
             siteInfo,
             sToken,
             {scale = 1, rect},
             experiments
           ]) => {
      settings.experiments = experiments;
      settings.siteUrl = siteInfo.url;

      settings.trackPage = 'widget-track.html';
      settings.featureDisabledPage = 'widget-feature-disabled.html';
      settings.fbRedirectPage = 'fb-redirect.html';

      settings.instanceId = instanceId;
      settings.compId = compId;
      settings.userId = userId;
      settings.biToken = biToken;
      settings.sToken = sToken;
      settings.siteOwnerId = WixSdk.Utils.getSiteOwnerId();
      settings.paymentsId = paymentsId;
      settings.isGetWindowEnabled = isGetWindowEnabled;

      settings.viewMode = WixSdk.Utils.getViewMode();
      settings.isMobileView = WixSdk.Utils.getDeviceType() === 'mobile';

      settings.locale = locale;
      settings.rect = rect;
      settings.scale = scale;
      settings.urlPrefixes = getUrlPrefixes();

      settings.width = getNormalizedWidth(player, settings);
      const autoplay = settings.booleans.autoplay;
      settings.autoplay = autoplay && WixSdk.Utils.getViewMode() !== 'editor' && isAppOnPage;

      ['colors', 'fonts'].forEach(prop => {
        settings[prop] = settings[prop] || {};
        try {
          let text = document.getElementById(`default-${prop}`).innerHTML.replace('/*', '').replace('*/', '');
          text = text.replace(/'.*?'/g, o => {
            return o.replace(/"/g, '\\"');
          }).replace(/'/g, '"');

          const obj = JSON.parse(text);
          Object.keys(obj).forEach(key => {
            settings[prop][key] = obj[key];
          });
        } catch (ex) {
          // expected
        }
      });
      settings.albumId = albumId;

      const hasPreviousSettings = !areObjectsEqual(prevSettings, {});
      if (hasPreviousSettings && prevSettings.booleans.showTrackList !== settings.booleans.showTrackList) {
        forceUpdate = true;
      }
      settings.shouldInitTracklistScroll = !forceUpdate;

      if (!areObjectsEqual(prevSettings, settings) || forceUpdate) {
        return reInitPlayer(settings, albumId);
      }
    })
    .then((player: any) => {
      if (!player || WixSdk.Utils.getViewMode() !== 'editor') {
        return;
      }

      const widgetRectHeight = settings.rect.height;

      if (!widgetRectHeight) {
        // prevent resizing if widget is hidden and its height is zero
        return;
      }

      if (player.getDimensions) {
        return siteStorage.get('numberTracksShown')
          .then(numberTracksShown => {
            if (prevSettings.numbers &&
              prevSettings.numbers.layout !== settings.numbers.layout) {
              resizeOnLayoutChange(settings, player);
            } else if (prevSettings.numbers &&
              prevSettings.numbers.listLayoutTrackStyle !== settings.numbers.listLayoutTrackStyle) {
              resizeOnTrackStyleChange(settings, player);
            } else {
              resizeOnReInit(settings, player, forceUpdate, numberTracksShown);
            }

            layoutValues[settings.numbers.layout || 0].width = settings.rect.width;
          });
      }

      return setWidgetHeightOld({settings, prevSettings, player, forceUpdate, widgetRectHeight});
    })
    .then(() => {
      prevSettings = cloneObject(settings);
    })
    .then(() => player)
    .catch(reason => {
      if (reason && reason.message === 'Browser Not Supported') {
        writeOops('Your browser is not supported');
        return Promise.reject(reason);
      }

      logError(BI_ERRORS.widget.init.reinit, {reason});
      return Promise.reject(reason);
    });
}

function reInitPlayer(settings, collectionId) {
  const element = document.querySelector('wix-pl-app, .wix-pl-app, #WixMusicPlayerWidget');
  return PL.init(element, {...settings, albumId: collectionId})
    .then(_player => {
      logPlayerStarted();

      exposePlayer(_player);
      subscribeToPlayerEvents(_player);
      if (_player.statisticEvents) {
        logPlayerEvents(_player.statisticEvents, {sToken: settings.sToken});
      }
      if (_player.initCurrentTrack) {
        _player.initCurrentTrack();
      }

      return new Promise(resolve => {
        requestAnimationFrame(() => {
          resolve(_player);
        });
      });
    })
}

function getPaymentsId() {
  return siteStorage.get('paymentsId');
}

function getIsGetWindowEnabled() {
  return siteStorage.getDataPublic('isGetWindowEnabled');
}
function exposePlayer(_player) {
  player = _player;
  return player;
}

function getAlbumId() {
  return Promise.all([WixSdk.getExternalId(), siteStorage.get('albumId'), getDemoAlbumId()])
    .then(([externalId, dataAlbumId, demoAlbumId]) => {
      const albumId = dataAlbumId || externalId;
      if (!albumId || albumId === NO_ALBUM_ID) {
        return demoAlbumId;
      }
      return albumId;
    });
}

function getDemoAlbumId() {
  return siteStorage.get('demoAlbumId')
    .then(demoAlbumId => demoAlbumId || DEMO_ALBUM_ID);
}



// WEBPACK FOOTER //
// ./widget-player/init-player.ts